import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function HeroSection() {
  return <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-764">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-765">
        {/* Left Column: Copy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-2xl" data-id="element-766">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-767">
            <span className="w-8 h-px bg-teal" data-id="element-768"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-769">
              Global Human-Capability Platform
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-[1.1] mb-6 text-balance" data-id="element-770">
            Building Better Judgment for a More Capable Society
          </h1>

          <p className="text-lg text-ink-muted mb-10 leading-relaxed max-w-xl" data-id="element-771">
            SyNeural is the parent platform behind CJI and NCJI — a global
            human-capability, civic-development, and leadership-readiness
            framework designed to strengthen reasoning, reduce manipulation
            vulnerability, align talent with opportunity, and improve
            decision-making from early development to executive leadership.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-772">
            <button className="bg-navy hover:bg-navy-light text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm flex items-center justify-center gap-2" data-id="element-773">
              Request a Demo
              <ArrowRight size={18} data-id="element-774" />
            </button>
            <button className="bg-transparent border border-navy text-navy hover:bg-surface-muted font-medium px-6 py-3.5 rounded-md transition-colors flex items-center justify-center" data-id="element-775">
              Explore the Platform
            </button>
          </div>

          <p className="text-xs text-ink-muted font-medium" data-id="element-776">
            For education, civil society, workforce systems, public
            institutions, and leadership development.
          </p>
        </motion.div>

        {/* Right Column: Abstract Diagram */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="relative h-[400px] lg:h-[500px] w-full flex items-center justify-center" data-id="element-777">
          <div className="absolute inset-0 bg-gradient-to-tr from-surface-bg via-white to-surface-muted rounded-full blur-3xl opacity-50 -z-10" data-id="element-778"></div>

          <svg viewBox="0 0 500 500" className="w-full h-full max-w-lg" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-779">
            {/* Background structural lines */}
            <circle cx="250" cy="250" r="200" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="4 8" data-id="element-780" />
            <circle cx="250" cy="250" r="140" stroke="#D9E2EC" strokeWidth="1" data-id="element-781" />
            <circle cx="250" cy="250" r="80" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="2 4" data-id="element-782" />

            {/* Connecting Pathways */}
            <path d="M100 350 C 150 350, 200 250, 250 250" stroke="#245B8F" strokeWidth="1.5" className="opacity-60" data-id="element-783" />
            <path d="M250 250 C 300 250, 350 150, 400 150" stroke="#2E8C88" strokeWidth="1.5" className="opacity-60" data-id="element-784" />
            <path d="M150 150 C 200 200, 250 250, 300 350" stroke="#123B5D" strokeWidth="1.5" className="opacity-40" data-id="element-785" />

            {/* Nodes & Labels */}
            {/* Individual */}
            <g transform="translate(100, 350)" data-id="element-786">
              <circle cx="0" cy="0" r="6" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-787" />
              <text x="-10" y="20" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-788">
                Individual
              </text>
            </g>

            {/* Education */}
            <g transform="translate(150, 150)" data-id="element-789">
              <circle cx="0" cy="0" r="8" fill="#FFFFFF" stroke="#6FAE8A" strokeWidth="2" data-id="element-790" />
              <text x="0" y="-15" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-791">
                Education
              </text>
            </g>

            {/* Workforce */}
            <g transform="translate(250, 250)" data-id="element-792">
              <circle cx="0" cy="0" r="12" fill="#FFFFFF" stroke="#123B5D" strokeWidth="2.5" data-id="element-793" />
              <circle cx="0" cy="0" r="4" fill="#123B5D" data-id="element-794" />
              <text x="20" y="4" fontSize="12" fill="#1F2A37" fontFamily="Inter" fontWeight="600" data-id="element-795">
                Workforce
              </text>
            </g>

            {/* Civil Society */}
            <g transform="translate(300, 350)" data-id="element-796">
              <circle cx="0" cy="0" r="10" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-797" />
              <text x="0" y="22" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-798">
                Civil Society
              </text>
            </g>

            {/* Government */}
            <g transform="translate(350, 220)" data-id="element-799">
              <circle cx="0" cy="0" r="14" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-800" />
              <text x="22" y="4" fontSize="11" fill="#5B6675" fontFamily="Inter" fontWeight="500" data-id="element-801">
                Government
              </text>
            </g>

            {/* Leadership */}
            <g transform="translate(400, 150)" data-id="element-802">
              <circle cx="0" cy="0" r="18" fill="#FFFFFF" stroke="#123B5D" strokeWidth="3" data-id="element-803" />
              <circle cx="0" cy="0" r="26" stroke="#2E8C88" strokeWidth="1" strokeDasharray="2 2" data-id="element-804" />
              <text x="0" y="-32" fontSize="12" fill="#1F2A37" fontFamily="Inter" textAnchor="middle" fontWeight="600" data-id="element-805">
                Leadership
              </text>
            </g>
          </svg>
        </motion.div>
      </div>
    </section>;
}